import type { DomRefs } from './dom';
import type { UpdateEvaluation } from '../manifest/load';
import type { ManifestUpdate } from '../manifest/schema';
import type { DeviceInfo } from '../ble/transport';
import type { OtaProgress, OtaStatus } from '../ota/types';
import type { OperationalTuningTargetConfig } from '../flows/retune-target-selection';
import { resolveActiveBladeAreaImageSrc } from '../active-blade-area';
import { renderReleaseNotes } from '../release-notes';
import { appendSupportLog } from '../support-log';

export function renderDeviceInfo(dom: DomRefs, info: DeviceInfo | null): void {
  dom.deviceName.textContent = info?.name ?? 'Not connected';
  dom.deviceId.textContent = info?.id ?? '--';
  dom.deviceFirmware.textContent = info?.firmwareRevision ?? '--';
  dom.deviceHardware.textContent = info?.hardwareRevision ?? '--';
  dom.deviceSerial.textContent = info?.serialNumber ?? '--';
  dom.deviceModel.textContent = info?.model ?? '--';
  dom.deviceCenterFrequency.textContent =
    info?.centerFrequency != null ? `${formatFrequencyHz(info.centerFrequency)} Hz` : '--';

  dom.devicePower.textContent =
    info?.powerWatts != null && Number.isFinite(info.powerWatts)
      ? `${info.powerWatts.toFixed(2)} W`
      : '--';

  dom.deviceBattery.textContent =
    info?.voltageBattery != null && Number.isFinite(info.voltageBattery)
      ? `${info.voltageBattery.toFixed(2)} V`
      : '--';

  dom.deviceTemperature.textContent = formatTemperatureSummary(info);
  dom.deviceOdometer.textContent =
    info?.odometerTotalMinutes != null && Number.isFinite(info.odometerTotalMinutes)
      ? `${Math.round(info.odometerTotalMinutes)} min`
      : '--';

  dom.diagnosticsCenterFrequency.textContent =
    info?.centerFrequency != null ? `${formatFrequencyHz(info.centerFrequency)} Hz` : '--';
  dom.diagnosticsBatteryVoltage.textContent =
    info?.voltageBattery != null && Number.isFinite(info.voltageBattery)
      ? `${info.voltageBattery.toFixed(2)} V`
      : '--';
  dom.diagnosticsTemperature.textContent =
    info?.temperaturePCB != null && Number.isFinite(info.temperaturePCB)
      ? `${info.temperaturePCB.toFixed(1)} °C`
      : '--';
}

function formatTemperatureSummary(info: DeviceInfo | null): string {
  if (!info) {
    return '--';
  }

  const parts: string[] = [];
  if (info.temperaturePCB != null && Number.isFinite(info.temperaturePCB)) {
    parts.push(`PCB ${info.temperaturePCB.toFixed(1)}°C`);
  }
  if (info.temperatureESP32 != null && Number.isFinite(info.temperatureESP32)) {
    parts.push(`ESP ${info.temperatureESP32.toFixed(1)}°C`);
  }
  if (info.temperatureBattery != null && Number.isFinite(info.temperatureBattery)) {
    parts.push(`Batt ${info.temperatureBattery.toFixed(1)}°C`);
  }

  return parts.length > 0 ? parts.join(' · ') : '--';
}

function formatFrequencyHz(value: number): string {
  return Math.round(value).toLocaleString('en-US');
}

export function renderFirmwareList(
  dom: DomRefs,
  evaluations: UpdateEvaluation[],
  selectedTag: string | null,
  currentFirmwareRevision: string | null = null
): void {
  const previousOthersPanel = dom.firmwareList.querySelector('.firmware-others') as HTMLDetailsElement | null;
  const previousOthersOpen = previousOthersPanel?.open ?? false;
  dom.firmwareList.innerHTML = '';
  const hasAny = evaluations.length > 0;
  dom.firmwareEmpty.hidden = hasAny;
  if (!hasAny) {
    return;
  }

  const recommendedEvaluation = getRecommendedEvaluation(evaluations, currentFirmwareRevision);
  const latestEligibleEvaluation = getLatestEligibleEvaluation(evaluations);
  const deviceAlreadyOnLatest =
    latestEligibleEvaluation !== null &&
    isCurrentFirmware(latestEligibleEvaluation.update, currentFirmwareRevision);
  const otherEvaluations = recommendedEvaluation
    ? evaluations.filter((evaluation) => evaluation !== recommendedEvaluation)
    : evaluations;

  if (recommendedEvaluation) {
    dom.firmwareList.appendChild(
      createFirmwareCard(recommendedEvaluation, selectedTag, { recommended: true })
    );
  } else if (deviceAlreadyOnLatest) {
    const message = document.createElement('p');
    message.className = 'panel-sub panel-sub--muted';
    message.textContent =
      'Your device is already running the latest firmware. There is no recommended update for your device.';
    dom.firmwareList.appendChild(message);
  }

  if (otherEvaluations.length > 0) {
    const details = document.createElement('details');
    details.className = 'firmware-others';
    details.open = previousOthersOpen;

    const summary = document.createElement('summary');
    summary.className = 'firmware-others__summary';
    summary.textContent = 'Other firmware versions';

    const list = document.createElement('div');
    list.className = 'firmware-others__list';
    otherEvaluations.forEach((evaluation) => {
      list.appendChild(
        createFirmwareCard(evaluation, selectedTag, {
          recommended: false,
          muted: evaluation.compatibility === 'compatible',
        })
      );
    });

    details.appendChild(summary);
    details.appendChild(list);
    dom.firmwareList.appendChild(details);
  }

  dom.firmwareList.appendChild(createChangelogLink());
}

export function renderStatus(dom: DomRefs, status: OtaStatus): void {
  dom.statusText.textContent = status.message;
  dom.statusText.dataset.level = status.level;
}

export function renderProgress(dom: DomRefs, progress: OtaProgress): void {
  const clamped = Math.max(0, Math.min(100, progress.percent));
  dom.progressBar.style.width = `${clamped}%`;
  dom.progressPercent.textContent = `${Math.round(clamped)}%`;
  dom.progressSpeed.textContent = `${progress.speedKbps.toFixed(1)} kB/s`;
  dom.progressBar.parentElement?.setAttribute('aria-valuenow', String(Math.round(clamped)));
}

export function appendLog(dom: DomRefs, entry: { level: 'info' | 'success' | 'error'; message: string }): void {
  appendSupportLog(
    'ui',
    'message',
    { message: entry.message },
    entry.level === 'error' ? 'error' : 'info'
  );
  const item = document.createElement('div');
  item.className = `log-item log-item--${entry.level}`;
  item.textContent = `${new Date().toLocaleTimeString()} · ${entry.message}`;
  dom.logList.prepend(item);
}

export function clearLog(dom: DomRefs): void {
  dom.logList.innerHTML = '';
}

export function renderRetuneTargetSelector(
  dom: DomRefs,
  input: {
    targets: OperationalTuningTargetConfig[];
    selectedTargetId: string | null;
    summary: string;
    disabled: boolean;
    hidden?: boolean;
  }
): void {
  const hidden = input.hidden ?? input.targets.length === 0;
  const summary = input.summary.trim();
  dom.retuneTargetPanel.hidden = hidden;
  dom.retuneTargetSummary.textContent = summary;
  dom.retuneTargetSummary.hidden = summary.length === 0;
  dom.retuneTargetOptions.replaceChildren();

  if (hidden) {
    return;
  }

  input.targets.slice().reverse().forEach((target) => {
    const label = document.createElement('label');
    label.className = 'retune-target-option';

    const radio = document.createElement('input');
    radio.type = 'radio';
    radio.name = 'retuneTarget';
    radio.value = target.target_id;
    radio.checked = input.selectedTargetId === target.target_id;
    radio.disabled = input.disabled;
    radio.setAttribute('aria-label', target.description);

    const content = document.createElement('div');
    content.className = 'retune-target-option__content';

    const imageSrc = resolveActiveBladeAreaImageSrc(target);
    const imageWrap = document.createElement('div');
    imageWrap.className = 'retune-target-option__blade-image';
    if (imageSrc) {
      const image = document.createElement('img');
      image.src = imageSrc;
      image.alt = `${target.description} active blade area`;
      image.loading = 'lazy';
      imageWrap.append(image);
    } else {
      imageWrap.classList.add('retune-target-option__blade-image--fallback');
      imageWrap.textContent = `${Math.round(target.center_frequency_hz / 1000)} kHz`;
    }

    const titleRow = document.createElement('div');
    titleRow.className = 'retune-target-option__title-row';

    const title = document.createElement('span');
    title.className = 'retune-target-option__title';
    title.textContent = target.description;
    titleRow.append(title);

    content.append(titleRow, imageWrap);
    label.append(radio, content);
    dom.retuneTargetOptions.append(label);
  });
}

function formatSize(bytes: number): string {
  if (!bytes || bytes <= 0) {
    return 'Size: --';
  }
  const kb = bytes / 1024;
  if (kb < 1024) {
    return `Size: ${kb.toFixed(1)} kB`;
  }
  const mb = kb / 1024;
  return `Size: ${mb.toFixed(2)} MB`;
}

type FirmwareCardOptions = {
  recommended: boolean;
  muted?: boolean;
};

function getRecommendedEvaluation(
  evaluations: UpdateEvaluation[],
  currentFirmwareRevision: string | null
): UpdateEvaluation | null {
  const latestEligible = getLatestEligibleEvaluation(evaluations);
  if (!latestEligible) {
    return null;
  }
  if (isCurrentFirmware(latestEligible.update, currentFirmwareRevision)) {
    return null;
  }
  return latestEligible;
}

function getLatestEligibleEvaluation(evaluations: UpdateEvaluation[]): UpdateEvaluation | null {
  let latest: UpdateEvaluation | null = null;
  let latestTimestamp = -Infinity;
  evaluations.forEach((evaluation) => {
    if (evaluation.compatibility === 'incompatible' || evaluation.update.size <= 0) {
      return;
    }
    const timestamp = parseUpdateDate(evaluation.update.date);
    if (timestamp > latestTimestamp) {
      latest = evaluation;
      latestTimestamp = timestamp;
    }
  });
  return latest;
}

function isCurrentFirmware(update: ManifestUpdate, currentFirmwareRevision: string | null): boolean {
  const normalizedCurrent = normalizeFirmwareValue(currentFirmwareRevision);
  if (!normalizedCurrent) {
    return false;
  }

  const normalizedVersion = normalizeFirmwareValue(update.version);
  const normalizedTag = normalizeFirmwareValue(update.tag);
  return normalizedCurrent === normalizedVersion || normalizedCurrent === normalizedTag;
}

function normalizeFirmwareValue(value: string | null | undefined): string {
  if (!value) {
    return '';
  }
  const normalized = value.trim().toLowerCase();
  return normalized.startsWith('v') ? normalized.slice(1) : normalized;
}

function parseUpdateDate(value: string): number {
  const timestamp = Date.parse(value);
  return Number.isNaN(timestamp) ? -Infinity : timestamp;
}

function createFirmwareCard(
  evaluation: UpdateEvaluation,
  selectedTag: string | null,
  options: FirmwareCardOptions
): HTMLDivElement {
  const { update, compatibility, reasons } = evaluation;
  const card = document.createElement('div');
  card.className = 'firmware-card';
  card.dataset.compat = compatibility;
  if (options.recommended) {
    card.classList.add('firmware-card--recommended');
  }
  if (options.muted) {
    card.classList.add('firmware-card--muted');
  }

  const header = document.createElement('div');
  header.className = 'firmware-card__header';

  const title = document.createElement('div');
  title.className = 'firmware-card__title';
  title.textContent = update.version;

  const badge = buildBadge(compatibility, options.recommended, options.muted ?? false);

  header.appendChild(title);
  if (badge) {
    header.appendChild(badge);
  }

  const meta = document.createElement('div');
  meta.className = 'firmware-card__meta';
  meta.textContent = `Tag ${update.tag} · ${update.date} · ${formatSize(update.size)}`;

  const notes = createReleaseNotesBlock(update.release_notes);

  const selectRow = document.createElement('label');
  selectRow.className = 'firmware-card__select';

  const input = document.createElement('input');
  input.type = 'radio';
  input.name = 'firmware-selection';
  input.value = update.tag;
  input.dataset.tag = update.tag;
  input.dataset.url = update.url;
  input.dataset.version = update.version;
  input.disabled = compatibility !== 'compatible' || update.size <= 0;
  input.checked = selectedTag === update.tag;

  const label = document.createElement('span');
  if (update.size <= 0) {
    label.textContent = 'Firmware asset is not available yet.';
  } else if (compatibility === 'compatible') {
    label.textContent = 'Select this firmware for update.';
  } else if (compatibility === 'unknown') {
    label.textContent = 'Connect your device to update.';
  } else {
    label.textContent = 'This firmware is not compatible with the connected device.';
  }

  selectRow.appendChild(input);
  selectRow.appendChild(label);

  card.appendChild(header);
  card.appendChild(meta);
  card.appendChild(notes);
  if (reasons.length > 0) {
    const reasonText = document.createElement('div');
    reasonText.className = 'firmware-card__notes';
    reasonText.textContent = reasons.join(' ');
    card.appendChild(reasonText);
  }
  card.appendChild(selectRow);

  return card;
}

function createReleaseNotesBlock(notesMarkdown: string): HTMLDivElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'firmware-card__release-notes';

  const { preview, remainder } = splitReleaseNotes(notesMarkdown);

  const previewBody = document.createElement('div');
  previewBody.className = 'release-notes release-notes--preview';
  renderReleaseNotes(previewBody, preview);
  wrapper.append(previewBody);

  if (!remainder) {
    return wrapper;
  }

  const remainderBody = document.createElement('div');
  remainderBody.className = 'release-notes release-notes--remainder';
  remainderBody.hidden = true;
  renderReleaseNotes(remainderBody, remainder);

  const toggle = document.createElement('button');
  toggle.type = 'button';
  toggle.className = 'firmware-card__release-notes-toggle';
  toggle.textContent = 'See More';
  toggle.setAttribute('aria-expanded', 'false');
  toggle.addEventListener('click', () => {
    const expanded = remainderBody.hidden;
    remainderBody.hidden = !expanded;
    toggle.textContent = expanded ? 'See Less' : 'See More';
    toggle.setAttribute('aria-expanded', String(expanded));
  });

  wrapper.append(toggle, remainderBody);
  return wrapper;
}

function splitReleaseNotes(notesMarkdown: string): { preview: string; remainder: string | null } {
  const trimmed = notesMarkdown.trim();
  const firstLineBreak = /\r?\n/.exec(trimmed);
  if (!firstLineBreak) {
    return { preview: trimmed, remainder: null };
  }

  const preview = trimmed.slice(0, firstLineBreak.index).trim();
  const remainder = trimmed.slice(firstLineBreak.index + firstLineBreak[0].length).trim();
  return {
    preview,
    remainder: remainder.length > 0 ? remainder : null,
  };
}

function createChangelogLink(): HTMLParagraphElement {
  const wrapper = document.createElement('p');
  wrapper.className = 'firmware-changelog-link';

  const link = document.createElement('a');
  link.className = 'support-link';
  link.href = './changelog.html';
  link.textContent = 'View full changelog';

  wrapper.append(link);
  return wrapper;
}

function buildBadge(
  compatibility: UpdateEvaluation['compatibility'],
  recommended: boolean,
  muted: boolean
): HTMLSpanElement | null {
  if (recommended) {
    const badge = document.createElement('span');
    badge.className = 'badge badge--recommended';
    badge.textContent = 'Recommended';
    return badge;
  }
  if (compatibility === 'incompatible') {
    const badge = document.createElement('span');
    badge.className = 'badge badge--error';
    badge.textContent = 'Incompatible';
    return badge;
  }
  if (compatibility === 'unknown') {
    const badge = document.createElement('span');
    badge.className = 'badge badge--warn';
    badge.textContent = 'Needs device info';
    return badge;
  }
  if (muted) {
    const badge = document.createElement('span');
    badge.className = 'badge badge--muted';
    badge.textContent = 'Compatible';
    return badge;
  }
  return null;
}
